import { HTTPResponse } from "@/class/HTTPResponse";
import { postData } from "../lib/api";
import React, { createContext, useState, ReactNode, useContext, useEffect } from "react";
import { Store } from "@/class/Store";
import {
  AUTH_KEYS,
  AUTH_UNAUTHORIZED_EVENT,
  clearAllAuthStorage,
  loadAuthState,
  persistAuthState,
  updateStoredStore,
} from "../lib/authStorage";

interface AuthProviderProps {
  children: ReactNode;
}

interface LoginResult {
  success: boolean;
  message?: string;
  role?: 'store' | 'system';
}

interface AuthContextType {
  isAuthenticated: boolean;
  userRole: 'store' | 'system' | null;
  store: Store;
  login: (loginId: string, password: string, rememberMe?: boolean) => Promise<LoginResult>;
  updateStore: (storeData: Store) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const initialState = loadAuthState();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(initialState.isAuthenticated);
  const [store, setStore] = useState<Store>(initialState.store);
  const [userRole, setUserRole] = useState<'store' | 'system' | null>(initialState.userRole);

  useEffect(() => {
    const handleUnauthorized = (): void => {
      setIsAuthenticated(false);
      setUserRole(null);
      setStore({} as Store);
    };

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);

    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    };
  }, []);

  const login = async (
    loginId: string,
    password: string,
    rememberMe = false
  ): Promise<LoginResult> => {
    clearAllAuthStorage();
    const storage = rememberMe ? localStorage : sessionStorage;

    if (loginId === 'admin' && password === 'pass') {
      try {
        const res: HTTPResponse = await postData('system-admin/auth', {
          login_id: loginId,
          password,
        });

        if (res?.data?.is_login === true) {
          setIsAuthenticated(true);
          setUserRole('system');
          setStore({} as Store);
          persistAuthState(storage, rememberMe, 'system', {} as Store, res.data.token);
          return { success: true, role: 'system' };
        }
      } catch (error: any) {
        return { success: false, message: error.message || 'サーバーエラーが発生しました。' };
      }
    }

    try {
      const res: HTTPResponse = await postData('store/auth', {
        login_id: loginId,
        password,
      });

      if (res?.data?.is_login === true) {
        setIsAuthenticated(true);
        setUserRole('store');
        setStore(res.data.store);
        persistAuthState(storage, rememberMe, 'store', res.data.store, res.data.token);
        return { success: true, role: 'store' };
      }

      setIsAuthenticated(false);
      setUserRole(null);
      setStore({} as Store);
      storage.setItem(AUTH_KEYS.isAuthenticated, 'false');
      return { success: false, message: '認証失敗' };
    } catch (error: any) {
      setIsAuthenticated(false);
      setUserRole(null);
      setStore({} as Store);
      storage.setItem(AUTH_KEYS.isAuthenticated, 'false');
      return { success: false, message: error.message || 'サーバーエラーが発生しました。' };
    }
  };

  const updateStore = (storeData: Store): void => {
    setStore(storeData);
    updateStoredStore(storeData);
  };

  const logout = (): void => {
    setIsAuthenticated(false);
    setUserRole(null);
    setStore({} as Store);
    clearAllAuthStorage();
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, userRole, store, login, updateStore, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
};
